const u = (code, dimension, system, baseFactor, de, en, aliases, scalableByDefault = true) => ({
    code,
    dimension,
    system,
    baseFactor,
    labels: {
        singular: { de: de[0], en: en[0] },
        plural: { de: de[1], en: en[1] },
        abbreviation: { de: de[2], en: en[2] },
    },
    aliases,
    scalableByDefault,
});
/**
 * Default unit catalog. The database copy (admin-maintained) is seeded from this list;
 * the domain layer uses it for parsing and display when no override is provided.
 */
export const DEFAULT_UNITS = [
    // Mass (base: g)
    u('mg', 'MASS', 'METRIC', '0.001', ['Milligramm', 'Milligramm', 'mg'], ['milligram', 'milligrams', 'mg'], ['mg', 'milligram', 'milligrams', 'milligramm']),
    u('g', 'MASS', 'METRIC', '1', ['Gramm', 'Gramm', 'g'], ['gram', 'grams', 'g'], ['g', 'gr', 'gram', 'grams', 'gramm', 'grs']),
    u('kg', 'MASS', 'METRIC', '1000', ['Kilogramm', 'Kilogramm', 'kg'], ['kilogram', 'kilograms', 'kg'], ['kg', 'kilo', 'kilos', 'kilogram', 'kilograms', 'kilogramm']),
    u('oz', 'MASS', 'US', '28.349523125', ['Unze', 'Unzen', 'oz'], ['ounce', 'ounces', 'oz'], ['oz', 'ounce', 'ounces', 'unze', 'unzen']),
    u('lb', 'MASS', 'US', '453.59237', ['Pfund', 'Pfund', 'lb'], ['pound', 'pounds', 'lb'], ['lb', 'lbs', 'pound', 'pounds', 'pfund']),
    // Volume (base: ml)
    u('ml', 'VOLUME', 'METRIC', '1', ['Milliliter', 'Milliliter', 'ml'], ['milliliter', 'milliliters', 'ml'], ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres']),
    u('cl', 'VOLUME', 'METRIC', '10', ['Zentiliter', 'Zentiliter', 'cl'], ['centiliter', 'centiliters', 'cl'], ['cl', 'centiliter', 'centiliters', 'zentiliter']),
    u('dl', 'VOLUME', 'METRIC', '100', ['Deziliter', 'Deziliter', 'dl'], ['deciliter', 'deciliters', 'dl'], ['dl', 'deciliter', 'deciliters', 'deziliter']),
    u('l', 'VOLUME', 'METRIC', '1000', ['Liter', 'Liter', 'l'], ['liter', 'liters', 'l'], ['l', 'liter', 'liters', 'litre', 'litres', 'ltr']),
    u('tsp', 'VOLUME', 'UNIVERSAL', '5', ['Teelöffel', 'Teelöffel', 'TL'], ['teaspoon', 'teaspoons', 'tsp'], ['tsp', 'tsps', 'teaspoon', 'teaspoons', 'tl', 'teelöffel', 'teeloeffel', 'teel.']),
    u('tbsp', 'VOLUME', 'UNIVERSAL', '15', ['Esslöffel', 'Esslöffel', 'EL'], ['tablespoon', 'tablespoons', 'tbsp'], [
        'tbsp',
        'tbsps',
        'tbs',
        'tbl',
        'tablespoon',
        'tablespoons',
        'el',
        'esslöffel',
        'essloeffel',
        'essl.',
    ]),
    u('cup', 'VOLUME', 'US', '236.5882365', ['Tasse', 'Tassen', 'Tasse'], ['cup', 'cups', 'cup'], ['cup', 'cups', 'c', 'tasse', 'tassen']),
    u('floz', 'VOLUME', 'US', '29.5735295625', ['Flüssigunze', 'Flüssigunzen', 'fl oz'], ['fluid ounce', 'fluid ounces', 'fl oz'], ['fl oz', 'floz', 'fl. oz', 'fl.oz', 'fluid ounce', 'fluid ounces']),
    u('pint', 'VOLUME', 'US', '473.176473', ['Pint', 'Pints', 'pt'], ['pint', 'pints', 'pt'], ['pint', 'pints', 'pt']),
    u('quart', 'VOLUME', 'US', '946.352946', ['Quart', 'Quarts', 'qt'], ['quart', 'quarts', 'qt'], ['quart', 'quarts', 'qt']),
    u('gallon', 'VOLUME', 'US', '3785.411784', ['Gallone', 'Gallonen', 'gal'], ['gallon', 'gallons', 'gal'], ['gallon', 'gallons', 'gal']),
    // Count (base: piece)
    u('piece', 'COUNT', 'UNIVERSAL', '1', ['Stück', 'Stück', 'Stk.'], ['piece', 'pieces', 'pc'], ['piece', 'pieces', 'pc', 'pcs', 'stück', 'stk', 'stk.', 'st.', 'x']),
    // Non-convertible kitchen measures
    u('pinch', 'OTHER', 'UNIVERSAL', '1', ['Prise', 'Prisen', 'Prise'], ['pinch', 'pinches', 'pinch'], ['pinch', 'pinches', 'prise', 'prisen'], false),
    u('dash', 'OTHER', 'UNIVERSAL', '1', ['Spritzer', 'Spritzer', 'Spritzer'], ['dash', 'dashes', 'dash'], ['dash', 'dashes', 'spritzer', 'schuss'], false),
    u('drop', 'OTHER', 'UNIVERSAL', '1', ['Tropfen', 'Tropfen', 'Tr.'], ['drop', 'drops', 'drop'], ['drop', 'drops', 'tropfen']),
    u('knifetip', 'OTHER', 'UNIVERSAL', '1', ['Messerspitze', 'Messerspitzen', 'Msp.'], ['knife tip', 'knife tips', 'knife tip'], ['msp', 'msp.', 'messerspitze', 'messerspitzen', 'knife tip'], false),
    u('handful', 'OTHER', 'UNIVERSAL', '1', ['Handvoll', 'Handvoll', 'Handvoll'], ['handful', 'handfuls', 'handful'], ['handful', 'handfuls', 'handvoll']),
    u('clove', 'OTHER', 'UNIVERSAL', '1', ['Zehe', 'Zehen', 'Zehe'], ['clove', 'cloves', 'clove'], ['clove', 'cloves', 'zehe', 'zehen', 'knoblauchzehe', 'knoblauchzehen']),
    u('bunch', 'OTHER', 'UNIVERSAL', '1', ['Bund', 'Bund', 'Bund'], ['bunch', 'bunches', 'bunch'], ['bunch', 'bunches', 'bund']),
    u('slice', 'OTHER', 'UNIVERSAL', '1', ['Scheibe', 'Scheiben', 'Scheibe'], ['slice', 'slices', 'slice'], ['slice', 'slices', 'scheibe', 'scheiben']),
    u('can', 'OTHER', 'UNIVERSAL', '1', ['Dose', 'Dosen', 'Dose'], ['can', 'cans', 'can'], ['can', 'cans', 'dose', 'dosen', 'tin', 'tins']),
    u('package', 'OTHER', 'UNIVERSAL', '1', ['Packung', 'Packungen', 'Pck.'], ['package', 'packages', 'pkg'], [
        'package',
        'packages',
        'packet',
        'packets',
        'pkg',
        'pck',
        'pck.',
        'packung',
        'packungen',
        'päckchen',
        'paeckchen',
        'pk',
    ]),
    u('jar', 'OTHER', 'UNIVERSAL', '1', ['Glas', 'Gläser', 'Glas'], ['jar', 'jars', 'jar'], ['jar', 'jars', 'glas', 'gläser', 'glaeser']),
    u('bottle', 'OTHER', 'UNIVERSAL', '1', ['Flasche', 'Flaschen', 'Fl.'], ['bottle', 'bottles', 'bottle'], ['bottle', 'bottles', 'flasche', 'flaschen']),
    u('stick', 'OTHER', 'UNIVERSAL', '1', ['Stange', 'Stangen', 'Stange'], ['stick', 'sticks', 'stick'], ['stick', 'sticks', 'stange', 'stangen', 'stalk', 'stalks']),
    u('sprig', 'OTHER', 'UNIVERSAL', '1', ['Zweig', 'Zweige', 'Zweig'], ['sprig', 'sprigs', 'sprig'], ['sprig', 'sprigs', 'zweig', 'zweige']),
    u('leaf', 'OTHER', 'UNIVERSAL', '1', ['Blatt', 'Blätter', 'Blatt'], ['leaf', 'leaves', 'leaf'], ['leaf', 'leaves', 'blatt', 'blätter', 'blaetter']),
    u('cube', 'OTHER', 'UNIVERSAL', '1', ['Würfel', 'Würfel', 'Würfel'], ['cube', 'cubes', 'cube'], ['cube', 'cubes', 'würfel', 'wuerfel']),
    u('sheet', 'OTHER', 'UNIVERSAL', '1', ['Platte', 'Platten', 'Platte'], ['sheet', 'sheets', 'sheet'], ['sheet', 'sheets', 'platte', 'platten']),
    u('sachet', 'OTHER', 'UNIVERSAL', '1', ['Beutel', 'Beutel', 'Btl.'], ['sachet', 'sachets', 'sachet'], ['sachet', 'sachets', 'beutel', 'btl', 'tütchen']),
    u('scoop', 'OTHER', 'UNIVERSAL', '1', ['Kugel', 'Kugeln', 'Kugel'], ['scoop', 'scoops', 'scoop'], ['scoop', 'scoops', 'kugel', 'kugeln']),
    // Length
    u('mm', 'LENGTH', 'METRIC', '1', ['Millimeter', 'Millimeter', 'mm'], ['millimeter', 'millimeters', 'mm'], ['mm']),
    u('cm', 'LENGTH', 'METRIC', '10', ['Zentimeter', 'Zentimeter', 'cm'], ['centimeter', 'centimeters', 'cm'], ['cm']),
    u('inch', 'LENGTH', 'US', '25.4', ['Zoll', 'Zoll', 'in'], ['inch', 'inches', 'in'], ['inch', 'inches', 'in', '"', 'zoll']),
];
const byCode = new Map(DEFAULT_UNITS.map((unit) => [unit.code, unit]));
export function getUnit(code) {
    return code ? byCode.get(code) : undefined;
}
export function unitLabel(code, locale, form = 'abbreviation') {
    const unit = byCode.get(code);
    if (!unit)
        return code;
    // For most units the abbreviation is what cooks expect; count-like units read better spelled out.
    if (unit.dimension === 'OTHER' || unit.code === 'piece' || unit.code === 'cup') {
        return unit.labels[form === 'abbreviation' ? 'plural' : form][locale];
    }
    return unit.labels.abbreviation[locale];
}
export const BASE_UNIT_BY_DIMENSION = {
    MASS: 'g',
    VOLUME: 'ml',
    COUNT: 'piece',
    LENGTH: 'mm',
};
//# sourceMappingURL=catalog.js.map